import { Divider, Dropdown, Modal, Popover, Radio, Skeleton, Spin, Tooltip } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Download,
  FileText,
  List,
  Lock,
  MoreVertical,
  RefreshCcw,
  Share2,
  Tool,
  Trash2,
  Unlock,
} from "react-feather";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useQueryClient } from "react-query";
import { useLocation, useParams } from "react-router-dom";
import { useTheme } from '../../hook/useTheme';
import filterDrive from "../../assets/images/filter-drive.svg";
import filterDriveDark from "../../assets/images/filter-drive-dark.svg";
import filterLocal from "../../assets/images/filter-local.svg";
import filterLocalDark from "../../assets/images/filter-local-dark.svg";
import AlbumGallery from "../../components/AlbumGallery";
import BannerAlbum from "../../components/Albums/BannerAlbum";
import CreateEditAlbum from "../../components/Albums/CreateEditAlbum";
import DownloadTxt from "../../components/Albums/DowloadTxt";
import DownloadPhotos from "../../components/Albums/DownloadPhotos";
import FilterAlbum from "../../components/Albums/FilterAlbum";
import ShareAlbum from "../../components/Albums/ShareAlbum";
import FolderAlbum from "../../components/FolderAlbum";
import MessageModal from "../../components/MessageModal";
import MetaTags from "../../components/MetaTags";
import { ACTION_ALBUM, PATH_ALBUM } from "../../constants/album";
import { SORT_ALBUM } from "../../constants/commons";
import { fetchAlbumSync, useGetAlbumApi, useLockApi } from "../../services/apis/album.api";
import { TAlbumDetail, TDownloadAlbum } from "../../types/album.type";
import FilterAndSaveAlbum from "../../components/Albums/FilterAndSaveAlbum";

const Album = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const location = useLocation();
  const queryClient = useQueryClient();
  const checkedPath = location.pathname.split("/")[1];
  const { ref, inView } = useInView();
  const [isModalOpenFilter, setIsModalOpenFilter] =
    useState<'drive' | 'local'>(null);
  const [isModalOpen, setIsModalOpen] = useState<`${ACTION_ALBUM}`>(null);
  const [sortMode, setSortMode] = useState<string>(SORT_ALBUM[0]);
  const [isFilter, setIsFilter] = useState(null);
  // const [facefinder, setFacefinder] = useState<boolean>(false);
  const [foldersId, setFoldersId] = useState<number[]>(undefined);
  const [isLoadingReload, setIsLoadingReload] = useState<boolean>(false);
  const [isModalOpenFace, setIsModalOpenFace] = useState<
    "confirm" | "notfound"
  >(undefined);
  const inputRefFace = useRef<HTMLInputElement>(null);
  const { mutate: mutateLock } = useLockApi();
  const { albumId } = useParams();
  const {
    data: albumDetail,
    isLoading: loadingAlbum,
    refetch: refetchAlbum,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetAlbumApi({
    filterParams: { ...isFilter },
    orderBy: !!sortMode ? sortMode : undefined,
    foldersId: foldersId,
  });
  const images = useMemo(() => {
    return albumDetail?.pages.reduce((acc, page) => {
      return [...acc, ...page?.data?.data?.files?.data];
    }, []);
  }, [albumDetail]);
  const albumDetailCache: TAlbumDetail =
    albumDetail?.pages[albumDetail.pages?.length === 1 ? 0 : 1]?.data?.data ||
    queryClient.getQueryData([`album_detail${albumId}`]);

  const onChangeSortMode = (e: any) => {
    setSortMode(e.target.value);
  };
  const handleLockAlbum = () => {
    albumDetailCache.is_locked = !albumDetailCache.is_locked;
    mutateLock(albumDetailCache?.id, {
      onSuccess: (res) => {
        albumDetailCache.is_locked = res.data.is_locked;
      },
      onError: () => {
        albumDetailCache.is_locked = !albumDetailCache.is_locked;
      },
    });
  };
  const contentSort = (
    <>
      <Radio.Group
        name="sort"
        className="flex flex-col gap-2"
        onChange={onChangeSortMode}
        value={sortMode}
      >
        {SORT_ALBUM?.map((mode, index) => (
          <Radio value={mode} key={mode}>
            {t(`albumDetail.sort.value${index + 1}`)}
          </Radio>
        ))}
      </Radio.Group>
      <Divider className="my-2" />
      <button
        onClick={() => {
          setSortMode("");
        }}
        disabled={!sortMode}
        className={`flex gap-2 items-center px-2 py-1 hover:bg-gray-200/50 dark:hover:bg-bg-tertiary rounded-lg w-full ${
          !!sortMode
            ? "cursor-pointer"
            : "bg-gray-200/50 dark:bg-bg-tertiary opacity-60 cursor-not-allowed"
        }`}
      >
        <Trash2 className="w-4" />
        {t("albumDetail.sort.deleteSort")}
      </button>
    </>
  );
  const statusDownload: Record<TDownloadAlbum, boolean> = {
    is_liked: !!albumDetail?.pages[0]?.data?.data?.like_count,
    tag_ids: albumDetail?.pages[0]?.data?.data?.tags?.length > 0,
    is_commented: !!albumDetail?.pages[0]?.data?.data?.comment_count,
    is_recommended: !!albumDetail?.pages[0]?.data?.data?.recommend_count,
  };

  // Dropdown menu items for "Tính năng khác"
  const moreFeaturesItems = [
    {
      key: 'share',
      label: (
        <div className="flex items-center gap-2 py-1">
          <Share2 className="w-4" />
          <span>{t("albumDetail.share")}</span>
        </div>
      ),
      onClick: () => setIsModalOpen(ACTION_ALBUM.SHARE),
    },
    ...(checkedPath.includes(PATH_ALBUM.DASHBOARD) ? [
      {
        key: 'filter-drive',
        label: (
          <div className="flex items-center gap-2 py-1">
            <img src={isDark ? filterDriveDark : filterDrive} alt="filter-drive" className="w-4" />
            <span>{t("albumDetail.filterDrive")}</span>
          </div>
        ),
        onClick: () => setIsModalOpenFilter('drive'),
      },
      {
        key: 'filter-local',
        label: (
          <div className="flex items-center gap-2 py-1">
            <img src={isDark ? filterLocalDark : filterLocal} alt="filter-local" className="w-4" />
            <span>{t("albumDetail.filterLocal")}</span>
          </div>
        ),
        onClick: () => setIsModalOpenFilter('local'),
      },
    ] : []),
    {
      key: 'view-list',
      label: (
        <div className="flex items-center gap-2 py-1">
          <FileText className="w-4" />
          <span>{t("albumDetail.viewList")}</span>
        </div>
      ),
      onClick: () => setIsModalOpen(ACTION_ALBUM.DOWNLOAD_TXT),
      disabled: (checkedPath.includes(PATH_ALBUM.ANO) &&
        (!albumDetailCache?.is_downloadable ||
          (albumDetailCache?.is_downloadable &&
            albumDetailCache?.file_count === 0))) ||
        Object.values(statusDownload).every((item) => !item),
    },
    {
      key: 'download',
      label: (
        <div className="flex items-center gap-2 py-1">
          <Download className="w-4" />
          <span>{t("albumDetail.download")}</span>
        </div>
      ),
      onClick: () => setIsModalOpen(ACTION_ALBUM.DOWNLOAD_PHOTOS),
      disabled: checkedPath.includes(PATH_ALBUM.ANO) &&
        (!albumDetailCache?.is_downloadable ||
          (albumDetailCache?.is_downloadable &&
            albumDetailCache?.file_count === 0)),
    },
  ];

  // const handleUploadAndCheckImages = async (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   if (event.target.files && event.target.files[0]) {
  //     setFacefinder(true);
  //     setFaceDetecting(true);
  //     await loadModels();
  //     const file = event.target.files[0];
  //     const labeledFaceDescriptor = await loadLabeledImages(file);
  //     if (labeledFaceDescriptor.descriptors.length === 0) {
  //       setIsModalOpenFace("notfound");
  //       setFaceDetecting(false);
  //       setFacefinder(false);
  //       return;
  //     }

  //     const response = await fetchAllImages(albumId);

  //     const matchedUrls = await checkLabeledImages(
  //       response.data.data.files.data || [],
  //       labeledFaceDescriptor
  //     );
  //     if (matchedUrls.length > 0) {
  //       setMatchedImages(matchedUrls);
  //     } else {
  //       setIsModalOpenFace("notfound");
  //     }

  //     setFaceDetecting(false);
  //     event.target.value = "";
  //   }
  // };

  useEffect(() => {

    if (inView && hasNextPage) {
        fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  // useEffect(() => {
  //   if (isLoadingReload) {
  //     if (!!foldersId && foldersId.length !== 0) setFoldersId([]);
  //     if (sortMode !== SORT_ALBUM[0]) setSortMode(SORT_ALBUM[0]);
  //     if (!!matchedImages) setMatchedImages(undefined);
  //     if (!!isFilter) setCheckedList(defaultCheckedList);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isLoadingReload]);

  return (
    <div className={`flex flex-col text-gray-900 dark:text-text-primary`}>
      {checkedPath.includes(PATH_ALBUM.DASHBOARD) && (
        <BannerAlbum
          isLoading={!albumDetailCache?.id}
          data={{
            customer_name: albumDetailCache?.customer_name,
            name: albumDetailCache?.name,
            thumbnail_url: albumDetailCache?.thumbnail_url,
          }}
        />
      )}
      {!albumDetailCache?.id ? (
        <div className="w-full overflow-hidden mt-4">
          <Skeleton active />
        </div>
      ) : (
        <>
          <div className="flex sm:flex-row flex-col justify-between items-center py-2 lg:py-4 border-b border-gray-200 dark:border-border-primary gap-1">
            <FilterAlbum
              setIsFilter={(values) => {
                setIsFilter(values);
              }}
              counts={{
                all: albumDetailCache?.file_count,
                comment: albumDetailCache?.comment_count,
                like: albumDetailCache?.like_count,
                recommend: albumDetailCache?.recommend_count,
              }}
              tags={albumDetailCache?.tags}
            />
            <div className="flex sm:space-x-2 space-x-1 text-sm items-center flex-wrap sm:flex-nowrap">
              <Popover content={contentSort} trigger="click">
                <div
                  className={`py-1 px-3 flex gap-1 items-center cursor-pointer transition-all rounded-3xl ${
                    !!sortMode
                      ? "text-secondary bg-[#0bcdac26] dark:bg-secondary/20 hover:bg-[#0bcdac26] dark:hover:bg-secondary/30"
                      : "hover:bg-gray-200/50 dark:hover:bg-bg-tertiary"
                  }`}
                >
                  <List className="w-4" />
                </div>
              </Popover>
              <Tooltip
                title={
                  t("albumDetail.updated") +
                  " " +
                  dayjs(albumDetailCache.updated_at)?.format(
                    "DD/MM/YYYY h:mm A"
                  )
                }
              >
                <button
                  className={`py-1 px-3 flex gap-1 items-center cursor-pointer transition-all rounded-3xl ${
                    isLoadingReload
                      ? "text-secondary bg-[#0bcdac26] dark:bg-secondary/20 hover:bg-[#0bcdac26] dark:hover:bg-secondary/30"
                      : "hover:bg-gray-200/50 dark:hover:bg-bg-tertiary"
                  }`}
                  onClick={async () => {
                    setIsLoadingReload(true);
                    await fetchAlbumSync(albumId);
                    await refetchAlbum();
                    setIsLoadingReload(false);
                  }}
                >
                  <RefreshCcw
                    className={`w-4 ${isLoadingReload ? "animate-spin" : ""}`}
                  />
                </button>
              </Tooltip>

              <Tooltip title={albumDetailCache?.is_locked ? t('albumDetail.album.locked') : null}>
                <div
                  onClick={() => {
                    checkedPath.includes(PATH_ALBUM.DASHBOARD) && handleLockAlbum();
                  }}
                  className={`py-1 px-3 flex gap-1 items-center transition-all rounded-3xl ${
                    checkedPath.includes(PATH_ALBUM.DASHBOARD)
                      ? "cursor-pointer hover:bg-gray-200/50 dark:hover:bg-bg-tertiary"
                      : ""
                  } ${albumDetailCache?.is_locked ? "bg-red-600 dark:bg-red-500 text-white" : ''}`}
                >
                  {albumDetailCache?.is_locked ? (
                    <Lock className="w-4" />
                  ) : (
                    <Unlock className="w-4" />
                  )}
                </div>
              </Tooltip>

              <Dropdown
                menu={{ items: moreFeaturesItems }}
                trigger={['click']}
                placement="bottom"
              >
                <div className="py-1 px-3 flex gap-1 items-center cursor-pointer transition-all hover:bg-gray-200/50 dark:hover:bg-bg-tertiary rounded-3xl">
                  <MoreVertical className="w-4" />
                  <span className="text-sm">{t("albumDetail.moreFeatures")}</span>
                </div>
              </Dropdown>

              {checkedPath.includes(PATH_ALBUM.DASHBOARD) && (
                <div
                  onClick={() => {
                    setIsModalOpen(ACTION_ALBUM.CREATE);
                  }}
                  className="cursor-pointer text-primary hover:opacity-85 transition-all flex items-end gap-1 py-1 px-3 rounded-3xl hover:bg-primary/10 dark:hover:bg-primary/20"
                >
                  <Tool className="w-4" />
                  {t("albumDetail.custom")}
                </div>
              )}
            </div>
          </div>

          {albumDetailCache?.folders?.length > 0 && (
            <div className="flex flex-col">
              <FolderAlbum
                folders={albumDetailCache?.folders}
                onClick={(data) => {
                  setFoldersId(data);
                }}
                loadingAlbum={loadingAlbum}
              />
            </div>
          )}
        </>
      )}

      <div className="py-4">
        {loadingAlbum ? (
          <div className="w-full overflow-hidden">
            <Skeleton active />
          </div>
        ) : (
          <AlbumGallery
            images={images || []}
            fetchNextPage={fetchNextPage}
            albumDetail={albumDetailCache}
            checkedPath={checkedPath}
            isCommentPassword={
              albumDetail?.pages[0]?.data?.data?.comment_password
            }
            isDownloadable={albumDetail?.pages[0]?.data?.data?.is_downloadable}
            maxFileLikes={albumDetail?.pages[0]?.data?.data?.max_file_likes}
          />
        )}
        <div ref={ref}>
          {isFetchingNextPage ? (
            <div className="py-6 w-full flex justify-center fixed bottom-0 left-1/2 -translate-x-1/2 bg-gradient-to-t from-white dark:from-bg-primary to-transparent">
              <Spin size="large" />
            </div>
          ) : !hasNextPage && images && images.length > 0 ? (
            <div className="py-4 w-full flex justify-center">
              <div className="text-gray-400 dark:text-gray-500 text-xs flex items-center gap-1">
                {t("albumDetail.allImagesLoaded")}
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
      <Modal
        title={
          <div className="text-primary font-medium text-2xl">
            {t("dashboard.albums.action.download.modalDownload.title")}
          </div>
        }
        open={isModalOpen?.includes(ACTION_ALBUM.DOWNLOAD_PHOTOS)}
        onCancel={() => {
          setIsModalOpen(null);
        }}
        footer={null}
        destroyOnClose
      >
        <DownloadPhotos
          id={albumDetail?.pages[0]?.data?.data?.id}
          tags={albumDetail?.pages[0]?.data?.data?.tags}
          status={statusDownload}
          totalPhotos={albumDetail?.pages[0]?.data?.data?.file_count}
          checkedPath={checkedPath}
        />
      </Modal>
      <Modal
        width={800}
        title={
          <div className="text-primary font-medium text-2xl">
            {t("dashboard.albums.action.download.txt.content")}
          </div>
        }
        open={isModalOpen?.includes(ACTION_ALBUM.DOWNLOAD_TXT)}
        onCancel={() => {
          setIsModalOpen(null);
        }}
        footer={null}
        destroyOnClose
      >
        <DownloadTxt
          id={albumDetail?.pages[0]?.data?.data?.id}
          tags={albumDetail?.pages[0]?.data?.data?.tags}
          status={statusDownload}
          totalPhotos={albumDetail?.pages[0]?.data?.data?.file_count}
        />
      </Modal>
      <Modal
        title={
          <div className="text-primary font-medium text-2xl">
            {t("dashboard.albums.action.download.modalShare.title")}
          </div>
        }
        open={isModalOpen?.includes(ACTION_ALBUM.SHARE)}
        onCancel={() => {
          setIsModalOpen(null);
        }}
        footer={null}
        destroyOnClose
      >
        <ShareAlbum albumId={albumDetailCache?.slug || albumDetailCache?.id} />
      </Modal>

      <Modal
        title={
          <div className="text-primary font-medium text-2xl">
            {isModalOpenFilter === "drive" ? t("dashboard.albums.action.download.modalFilterDrive.title") : t("dashboard.albums.action.download.modalFilterLocal.title")}
          </div>
        }
        open={!!isModalOpenFilter}
        onCancel={() => {
          setIsModalOpenFilter(null);
        }}
        footer={null}
        destroyOnClose
      >
        <FilterAndSaveAlbum
          type={isModalOpenFilter}
          album={albumDetailCache}
        />
      </Modal>
      <Modal
        title={
          <div className="text-primary font-semibold text-3xl">
            {t("formEditAlbum.title")}
          </div>
        }
        open={isModalOpen?.includes(ACTION_ALBUM.CREATE)}
        onCancel={() => {
          setIsModalOpen(null);
        }}
        footer={null}
        destroyOnClose
      >
        <CreateEditAlbum
          dfAlbum={albumDetailCache}
          onClick={(data) => {
            setIsModalOpen(null);
            if (!!data) refetchAlbum();
          }}
        />
      </Modal>
      <MessageModal
        isVisible={isModalOpenFace === "notfound"}
        onClose={() => {
          setIsModalOpenFace(undefined);
        }}
        title={t("albumDetail.face.title")}
        subTitle={t("albumDetail.face.faceFailed")}
        type="error"
        btnText={t("formCreateAlbum.createSuccess.btn")}
      />
      <MessageModal
        isVisible={isModalOpenFace === "confirm"}
        onClose={() => {
          setIsModalOpenFace(undefined);
        }}
        title={t("albumDetail.face.titleConfirm")}
        subTitle={t("albumDetail.face.subTitleConfirm")}
        type="confirm"
        btnText={
          <div
            className="cursor-pointer"
            onClick={() => {
              setIsModalOpenFace(undefined);
              if (inputRefFace) inputRefFace?.current?.click();
            }}
          >
            {t("albumDetail.face.searchBtn")}
          </div>
        }
      />
      <MetaTags
        title={albumDetailCache?.name}
        image={albumDetailCache?.thumbnail_url || ""}
        name="Shotpik"
      />
    </div>
  );
};

export default Album;
